import type { IdentityBrokerState, ShopCustomer } from './identityTypes';

const IDENTITY_SCHEMA_VERSION = 1;

interface StoredIdentitySnapshot {
  version: number;
  updatedAt: string;
  shopCustomersByShopId: Record<string, ShopCustomer>;
}

function storageKey(firebaseUid: string): string {
  return `commerce.identity.v${IDENTITY_SCHEMA_VERSION}:${firebaseUid}`;
}

function browserStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

export function readIdentitySnapshot(
  firebaseUid: string
): Pick<IdentityBrokerState, 'shopCustomersByShopId'> {
  const storage = browserStorage();
  if (!storage) return { shopCustomersByShopId: {} };

  try {
    const raw = storage.getItem(storageKey(firebaseUid));
    if (!raw) return { shopCustomersByShopId: {} };
    const parsed = JSON.parse(raw) as Partial<StoredIdentitySnapshot>;
    if (parsed.version !== IDENTITY_SCHEMA_VERSION) {
      return { shopCustomersByShopId: {} };
    }
    return {
      shopCustomersByShopId: parsed.shopCustomersByShopId ?? {},
    };
  } catch {
    return { shopCustomersByShopId: {} };
  }
}

export function writeIdentitySnapshot(
  firebaseUid: string,
  shopCustomersByShopId: Record<string, ShopCustomer | null>
): void {
  const storage = browserStorage();
  if (!storage) return;

  const compact: Record<string, ShopCustomer> = {};
  for (const [shopId, customer] of Object.entries(shopCustomersByShopId)) {
    if (customer) compact[shopId] = customer;
  }

  const payload: StoredIdentitySnapshot = {
    version: IDENTITY_SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    shopCustomersByShopId: compact,
  };

  try {
    storage.setItem(storageKey(firebaseUid), JSON.stringify(payload));
  } catch {
    /* localStorage quota/security errors should not break auth */
  }
}

export function clearIdentitySnapshot(firebaseUid: string): void {
  const storage = browserStorage();
  if (!storage) return;
  try {
    storage.removeItem(storageKey(firebaseUid));
  } catch {
    /* ignore */
  }
}
